import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, TrendingUp, ArrowDownCircle, ArrowUpCircle, Users, Handshake,
  ListChecks, PieChart, Settings, Plus, LogOut, Menu, X, Shield, UserCog, CalendarDays, LineChart, UserCircle, Wrench, Stethoscope, ArrowLeftRight, Armchair, ShieldCheck, Receipt, BookOpen, Gauge, FileCheck2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import AddDialog from "@/components/AddDialog";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const NAV = [
  { to: "/", label: "Accueil", icon: Home },
  { to: "/previsions", label: "Prévisions", icon: TrendingUp },
  { to: "/encaissements", label: "Encaissements", icon: ArrowDownCircle },
  { to: "/depenses", label: "Dépenses", icon: ArrowUpCircle },
  { to: "/equipe", label: "Équipe / Salaires", icon: Users },
  { to: "/retrocessions", label: "Rétrocessions", icon: Handshake },
  { to: "/mouvements", label: "Mouvements", icon: ListChecks },
  { to: "/analyses", label: "Analyses", icon: PieChart },
  { to: "/parametres", label: "Paramètres", icon: Settings },
];
const ADMIN_ITEM = { to: "/admin", label: "Administration", icon: Shield };
const PROFILS_ITEM = { to: "/profils", label: "Profils", icon: UserCog };
const SECRETARY_ITEM = { to: "/saisie", label: "Saisie du jour", icon: CalendarDays };
const PRACTITIONER_ITEM = { to: "/mon-ca", label: "Mon chiffre d'affaires", icon: LineChart };
const MON_PROFIL_ITEM = { to: "/profil", label: "Mon profil", icon: UserCircle };
const RH_ITEM = { to: "/rh", label: "Ressources humaines", icon: Users };
const MAINT_ITEM = { to: "/maintenance", label: "Parc & Maintenance", icon: Wrench };
const CABINET_ITEM = { to: "/mon-cabinet", label: "Mon cabinet", icon: Armchair };
const COMP_ITEM = { to: "/conformite", label: "Conformité & Contrôles", icon: ShieldCheck };
const FACT_ITEM = { to: "/factures", label: "Factures", icon: Receipt };
const COMPTA_ITEM = { to: "/comptabilite", label: "Pré-comptabilité", icon: BookOpen };
const PILOT_ITEM = { to: "/pilotage", label: "Pilotage financier", icon: Gauge };
const TP_ITEM = { to: "/tiers-payant", label: "Rapprochement tiers payant", icon: FileCheck2 };
const withFactures = (nav) => { const e = nav.findIndex((n) => n.to === "/encaissements"); const n2 = [...nav.slice(0, e + 1), TP_ITEM, ...nav.slice(e + 1)]; const i = n2.findIndex((n) => n.to === "/depenses"); return [...n2.slice(0, i + 1), FACT_ITEM, COMPTA_ITEM, PILOT_ITEM, ...n2.slice(i + 1)]; };

function SpaceSwitch({ compact, onDone }) {
  const { user, space, setSpace } = useAuth();
  const navigate = useNavigate();
  if (!user?.practitioner_id || user.role === "practitioner") return null;
  const toPrat = space !== "praticien";
  const go = () => { setSpace(toPrat ? "praticien" : "gestion"); navigate(toPrat ? "/mon-ca" : "/"); onDone && onDone(); };
  const label = toPrat ? "Mon espace praticien" : "Retour à la gestion";
  const Icon = toPrat ? Stethoscope : ArrowLeftRight;
  return (
    <button type="button" onClick={go} data-testid={toPrat ? "switch-to-praticien" : "switch-to-gestion"} title={label} aria-label={label}
      className={`flex items-center gap-2 rounded-xl border border-[#B08D57]/40 bg-[#B08D57]/5 text-sm font-semibold text-[#8A6D3F] transition-colors hover:bg-[#B08D57]/15 ${compact ? "justify-center p-2.5" : "px-3.5 py-2.5"}`}>
      <Icon className="h-[18px] w-[18px] shrink-0" />{!compact && label}
    </button>
  );
}

const MOBILE_MAIN = [NAV[0], NAV[1], null, NAV[6], null];
const CONFIG_PATHS = ["/parametres", "/profils", "/admin", "/profil"];
const ADMIN_PATHS = ["/rh", "/maintenance", "/conformite"];
const sectionsOf = (nav) => [
  ["Finances", nav.filter((n) => !CONFIG_PATHS.includes(n.to) && !ADMIN_PATHS.includes(n.to))],
  ["Administration", nav.filter((n) => ADMIN_PATHS.includes(n.to))],
  ["Configuration", nav.filter((n) => CONFIG_PATHS.includes(n.to))],
].filter(([, items]) => items.length);

function SidebarLink({ item, onClick, compact }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onClick}
      title={compact ? item.label : undefined}
      aria-label={compact ? item.label : undefined}
      data-testid={`nav-${item.to.replace("/", "") || "home"}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-xl text-sm font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#065F46]/40 ${compact ? "justify-center p-3" : "px-3.5 py-2.5"} ${
          isActive ? "bg-[#065F46] text-white shadow-sm" : "text-slate-600 hover:bg-emerald-50 hover:text-[#065F46]"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      {!compact && item.label}
    </NavLink>
  );
}

function SideNav({ nav, compact }) {
  return (
    <nav aria-label="Navigation principale" className="mt-5 flex-1 space-y-5 overflow-y-auto pr-1">
      {sectionsOf(nav).map(([title, items]) => (
        <div key={title}>
          {compact ? <div className="mx-auto mb-2 h-px w-8 bg-gray-100" /> : <p className="mb-1.5 px-3.5 text-[10.5px] font-bold uppercase tracking-[0.18em] text-[#B08D57]">{title}</p>}
          <div className="space-y-0.5">{items.map((n) => <SidebarLink key={n.to} item={n} compact={compact} />)}</div>
        </div>
      ))}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, logout, space } = useAuth();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const pratMode = user?.role === "practitioner" || (user?.practitioner_id && space === "praticien");
  const role = pratMode ? "practitioner" : user?.role;
  const isOwner = user?.is_superadmin || ["owner", "admin"].includes(role);
  const restricted = ["secretary", "practitioner", "hr", "hygiene"].includes(role);
  const nav = restricted
    ? role === "practitioner"
      ? [PRACTITIONER_ITEM, CABINET_ITEM, MAINT_ITEM, COMP_ITEM, MON_PROFIL_ITEM]
      : role === "hygiene" ? [COMP_ITEM, MAINT_ITEM, MON_PROFIL_ITEM]
      : [role === "secretary" ? SECRETARY_ITEM : RH_ITEM, ...(role === "hr" ? [] : [MAINT_ITEM]), COMP_ITEM, MON_PROFIL_ITEM]
    : isOwner
    ? [...withFactures(NAV).flatMap((n, i) => (i === 0 ? [n, SECRETARY_ITEM] : [n])), RH_ITEM, MAINT_ITEM, COMP_ITEM, PROFILS_ITEM, ...(user?.is_superadmin ? [ADMIN_ITEM] : []), MON_PROFIL_ITEM]
    : [...NAV, ...(role === "manager" ? [RH_ITEM] : []), MAINT_ITEM, COMP_ITEM, MON_PROFIL_ITEM];

  if (restricted) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-100 bg-white/90 px-4 py-2.5 backdrop-blur-xl">
          <Brand home={nav[0].to} size="sm" />
          <div className="flex items-center gap-3">
            <SpaceSwitch compact />
            <NavLink to="/profil" data-testid="nav-profil-restricted" className="hidden text-sm font-semibold text-gray-500 hover:text-[#065F46] sm:inline">{user?.name}</NavLink>
            <button aria-label="Se déconnecter" data-testid="logout-button-restricted" onClick={() => { logout(); navigate("/login"); }} className="text-gray-400"><LogOut className="h-5 w-5" /></button>
          </div>
        </header>
        {(
          <nav aria-label="Navigation" className="border-b border-gray-100 bg-white" data-testid="restricted-nav">
            <div className="mx-auto flex max-w-5xl gap-1 overflow-x-auto px-4 py-2">
              {nav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={n.to} data-testid={`tab-${n.to.replace("/", "")}`}
                    className={({ isActive }) => `flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-[#065F46] text-white" : "text-slate-600 hover:bg-emerald-50 hover:text-[#065F46]"}`}>
                    <Icon className="h-4 w-4" />{n.label}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        )}
        <main className="mx-auto max-w-6xl px-4 py-6">{children}</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-72 flex-col border-r border-gray-100 bg-white px-4 pb-5 pt-5 lg:flex" data-testid="sidebar">
        <div className="flex min-h-[112px] items-center border-b border-[#B08D57]/20 px-2 pb-4">
          <Brand home="/" />
        </div>
        <SideNav nav={nav} />
        <div className="mt-3"><SpaceSwitch /></div>
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="flex items-center gap-3 px-3">
            <NavLink to="/profil" data-testid="nav-profil" className="flex min-w-0 flex-1 items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 font-bold text-[#065F46]">
                {(user?.name || user?.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{user?.name || "Utilisateur"}</p>
                <p className="truncate text-xs text-gray-400">{user?.email}</p>
              </div>
            </NavLink>
            <button aria-label="Se déconnecter" data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }} className="rounded-lg p-1 text-gray-400 hover:text-rose-600 focus-visible:ring-2 focus-visible:ring-rose-300">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
          <a href="/mentions-legales" data-testid="nav-mentions-legales" className="mt-3 block px-3 text-[11px] text-gray-400 transition-colors hover:text-[#065F46]">Mentions légales & CGU</a>
        </div>
      </aside>

      {/* Tablet compact sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-20 flex-col items-stretch border-r border-gray-100 bg-white px-2.5 pb-4 pt-4 md:flex lg:hidden" data-testid="sidebar-compact">
        <div className="flex justify-center border-b border-[#B08D57]/20 pb-3"><Brand home="/" compact /></div>
        <SideNav nav={nav} compact />
        <div className="mt-2 flex justify-center"><SpaceSwitch compact /></div>
        <button aria-label="Se déconnecter" onClick={() => { logout(); navigate("/login"); }} className="mx-auto mt-3 rounded-lg p-2 text-gray-400 hover:text-rose-600"><LogOut className="h-5 w-5" /></button>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-gray-100 bg-white/90 px-4 py-2.5 backdrop-blur-xl md:hidden">
        <Brand home="/" size="sm" />
        <div className="flex items-center gap-3">
          <SpaceSwitch compact />
          <button aria-label="Se déconnecter" data-testid="logout-button-mobile" onClick={() => { logout(); navigate("/login"); }} className="text-gray-400">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="md:pl-20 lg:pl-72">
        <div className="mx-auto max-w-5xl px-4 pb-32 pt-5 sm:px-6 md:pb-12 lg:pt-8">{children}</div>
      </main>

      {/* Mobile bottom nav */}
      <nav aria-label="Navigation mobile" className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-100 bg-white/85 backdrop-blur-xl md:hidden">
        <div className="mx-auto flex max-w-md items-center justify-around px-2 py-2" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
          {MOBILE_MAIN.map((item, i) => {
            if (item === null && i === 2) {
              return (
                <button
                  key="fab"
                  data-testid="fab-add-btn"
                  onClick={() => setAddOpen(true)}
                  className="-mt-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#065F46] text-white shadow-[0_10px_30px_rgba(6,95,70,0.4)] active:scale-95"
                >
                  <Plus className="h-7 w-7" />
                </button>
              );
            }
            if (item === null) {
              return (
                <Sheet key="more" open={moreOpen} onOpenChange={setMoreOpen}>
                  <SheetTrigger asChild>
                    <button data-testid="nav-more" className="flex flex-col items-center gap-1 px-3 py-1 text-gray-400">
                      <Menu className="h-5 w-5" />
                      <span className="text-[10px] font-semibold">Plus</span>
                    </button>
                  </SheetTrigger>
                  <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-3xl">
                    <SheetTitle className="sr-only">Menu IDA GRAY</SheetTitle>
                    <div className="mx-auto max-w-md space-y-4">
                      <Brand home="/" size="sm" />
                      {sectionsOf(nav).map(([title, items]) => (
                      <div key={title}>
                      <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.18em] text-[#B08D57]">{title}</p>
                      <div className="grid grid-cols-3 gap-3">
                        {items.map((n) => {
                          const Icon = n.icon;
                          return (
                            <NavLink
                              key={n.to}
                              to={n.to}
                              end={n.to === "/"}
                              onClick={() => setMoreOpen(false)}
                              className="flex flex-col items-center gap-2 rounded-2xl bg-gray-50 p-4 text-center text-xs font-semibold text-gray-700"
                            >
                              <Icon className="h-6 w-6 text-[#065F46]" />
                              {n.label}
                            </NavLink>
                          );
                        })}
                      </div>
                      </div>
                      ))}
                    </div>
                  </SheetContent>
                </Sheet>
              );
            }
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobilenav-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 px-3 py-1 ${isActive ? "text-[#065F46]" : "text-gray-400"}`
                }
              >
                <Icon className="h-5 w-5" />
                <span className="text-[10px] font-semibold">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Desktop FAB */}
      <button
        data-testid="fab-add-btn-desktop"
        onClick={() => setAddOpen(true)}
        className="fixed bottom-8 right-8 z-30 hidden h-14 items-center gap-2 rounded-full bg-[#065F46] px-6 font-semibold text-white shadow-[0_10px_30px_rgba(6,95,70,0.4)] transition-transform hover:-translate-y-0.5 active:scale-95 md:flex"
      >
        <Plus className="h-5 w-5" /> Ajouter
      </button>

      <AddDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}
